// @ts-nocheck
import React, { useRef, useState } from 'react';
import { ArrowLeft, AlertTriangle, AlertCircle, Check, Trash2, ArrowLeftRight, TrendingUp, Activity, GitBranch, BarChart3, HelpCircle, Edit3, Upload, FileJson, ListChecks } from 'lucide-react';

const QoEAnalysisView = ({
  onBackToResults,
  qoeResults,
  selectedQoeItem,
  setSelectedQoeItem,
  glResults,
  glAnalyzing,
  selectedAccountTransactions,
  setSelectedAccountTransactions,
  glSortField,
  setGlSortField,
  glSortAsc,
  setGlSortAsc,
  highlightedPeriodIndex,
  setHighlightedPeriodIndex,
  periodTooltip,
  setPeriodTooltip,
  formatQoeCurrency,
  getCategoryInfo,
  getItemAdjustments,
  getCategorySortOrder,
  getAdjustmentColor,
  getGlMatch,
  getAccountTransactions,
  getAccountAdjustments,
  sortGlTransactions,
  // Upload a GL file to populate transactions — used when glResults is empty
  // (e.g. loaded session that never had phase-2 drill-down data persisted).
  onGlFileUpload,
  originalGlFileName,
  // Optional: when present, shows an "Open in Workbench" button in the header
  // that hands the user to the workbench page for the current session.
  onOpenWorkbench,
  workbenchAvailable = false,
}) => {
  const glUploadInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleGlUploadPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && onGlFileUpload) onGlFileUpload(file);
  };

  const handleGlDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && onGlFileUpload) onGlFileUpload(file);
  };

  // Build a single combined JSON payload: every candidate + its reasoning + any
  // matched GL account + its transactions + any classified adjustments.
  const handleExportJson = () => {
    try {
      const candidates = (qoeResults.flagged_items || []).map((item) => {
        const name = item.original_name;
        const match = getGlMatch ? getGlMatch(name) : null;
        const txnBreakdown = getAccountTransactions ? getAccountTransactions(name) : null;
        const accountAdjustments = getAccountAdjustments ? getAccountAdjustments(name) : [];
        return {
          original_name: name,
          standardized_category: item.standardized_category,
          confidence: item.confidence,
          flag_reason: item.flag_reason,
          total_value: item.total_value,
          period_values: item.period_values,
          spiked_periods: item.spiked_periods,
          adjustments_from_flagging: item.adjustments || [],
          gl_match: match
            ? { account_code: match.gl_account_code, account_name: match.gl_account_name }
            : null,
          gl_transactions: txnBreakdown?.transactions || [],
          gl_transaction_count: txnBreakdown?.transaction_count ?? (txnBreakdown?.transactions?.length || 0),
          gl_adjustments: accountAdjustments,
        };
      });

      const payload = {
        exported_at: new Date().toISOString(),
        total_candidates: candidates.length,
        total_flagged_value: qoeResults.total_flagged_value ?? null,
        summary: qoeResults.summary ?? null,
        adjustment_summary: glResults?.adjustment_summary ?? null,
        unmatched_items: glResults?.unmatched_items ?? [],
        gl_file: qoeResults.gl_file_info ?? null,
        candidates,
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qoe-analysis-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export QoE JSON:', err);
      alert(`Failed to export QoE JSON: ${err.message || err}`);
    }
  };
  if (!qoeResults) return null;

  return (
    <div className="qoe-full-page">
      {/* Header */}
      <div className="qoe-full-header">
        <div className="qoe-header-left">
          <button onClick={onBackToResults} className="qoe-back-button">
            <ArrowLeft size={16} />
            Back to Results
          </button>
          <AlertTriangle size={18} className="qoe-icon" />
          <h3>QoE Adjustment Candidates</h3>
          <span className="qoe-count">{qoeResults.flagged_items?.length || 0} items flagged</span>
        </div>
        <div className="qoe-header-right">
          <span className="qoe-total-value">
            Total: {formatQoeCurrency(qoeResults.total_flagged_value)}
          </span>
          {glAnalyzing && (
            <span className="gl-status gl-status-loading">
              <div className="button-spinner"></div>
              Processing GL transactions…
            </span>
          )}
          {glResults && !glAnalyzing && (() => {
            const matchCount = glResults?.gl_matches?.length || 0;
            const txnCount = (glResults?.account_transactions || []).reduce(
              (n, b) => n + (b?.transactions?.length || 0),
              0
            );
            return (
              <span
                className="gl-status gl-status-loaded"
                title={`${txnCount.toLocaleString()} transactions across ${matchCount} accounts — cached in this session, no re-upload needed.`}
              >
                <Check size={14} />
                Transactions cached · {matchCount} accounts
              </span>
            );
          })()}
          {!glResults && !glAnalyzing && (
            <span
              className="gl-status gl-status-missing"
              title="The GL transactions for this session weren't saved. Upload the General Ledger below to populate them — they'll be cached for future loads of this session."
            >
              <AlertCircle size={14} />
              Transactions not cached
            </span>
          )}
          <button
            onClick={handleExportJson}
            className="qoe-export-json-button"
            disabled={!qoeResults?.flagged_items?.length}
            title="Download every candidate with reasoning and matched GL transactions as a JSON file"
          >
            <FileJson size={14} />
            Export JSON
          </button>
          {onOpenWorkbench && (
            <button
              onClick={onOpenWorkbench}
              className="qoe-open-workbench-button"
              disabled={!workbenchAvailable}
              title={
                workbenchAvailable
                  ? 'Review approved adjustments and build the Adjusted EBITDA bridge'
                  : 'Save this session first so the workbench can attach to it'
              }
            >
              <ListChecks size={14} />
              Open Workbench
            </button>
          )}
        </div>
      </div>

      {/* Body: Sidebar + Detail */}
      <div className="qoe-full-body">
        {qoeResults.flagged_items && qoeResults.flagged_items.length > 0 ? (
          <>
            {/* Left Sidebar - Candidate Selector */}
            <div className="qoe-full-sidebar">
              <div className="qoe-sidebar-header">Candidates</div>
              <div className="qoe-candidate-list">
                {[...qoeResults.flagged_items]
                  .sort((a, b) => {
                    const tierDiff = getCategorySortOrder(a) - getCategorySortOrder(b);
                    if (tierDiff !== 0) return tierDiff;
                    return Math.abs(b.total_value || 0) - Math.abs(a.total_value || 0);
                  })
                  .map((item, idx) => (
                    <div
                      key={idx}
                      className={`qoe-candidate-item ${selectedQoeItem?.original_name === item.original_name ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedQoeItem(item);
                        setHighlightedPeriodIndex(null);
                        setPeriodTooltip({ visible: false, index: null, period: '', value: 0, x: 0, y: 0 });
                        if (glResults) {
                          setSelectedAccountTransactions(getAccountTransactions(item.original_name));
                        }
                      }}
                    >
                      <div className="qoe-candidate-row">
                        <span className="qoe-candidate-name" title={item.original_name}>{item.original_name}</span>
                      </div>
                      <div className="qoe-candidate-meta">
                        <span className="qoe-candidate-value">{formatQoeCurrency(item.total_value)}</span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>

            {/* Right Panel - Detail View */}
            <div className="qoe-full-detail">
              {selectedQoeItem ? (
                <>
                  {/* Scrollable upper region: header, reason, adjustment details */}
                  <div className="qoe-detail-scroll">
                  <div className="qoe-detail-top">
                    {/* Detail Header */}
                    <div className="qoe-detail-header">
                      <div className="qoe-detail-title-row">
                        <h4 className="qoe-detail-name">{selectedQoeItem.original_name}</h4>
                      </div>
                      <div className="qoe-detail-meta-row">
                        <span className="qoe-detail-category">{selectedQoeItem.standardized_category}</span>
                        <span className="qoe-detail-value">{formatQoeCurrency(selectedQoeItem.total_value)}</span>
                        <span className="qoe-detail-confidence">{Math.round(selectedQoeItem.confidence * 100)}% confidence</span>
                      </div>
                    </div>

                    {/* Reason */}
                    <div className="qoe-detail-reason">
                      <span className="reason-label">Reason:</span> {selectedQoeItem.flag_reason}
                    </div>

                    {/* Adjustment Details */}
                    {(() => {
                      const adjs = getItemAdjustments(selectedQoeItem);
                      if (adjs.length === 0) return null;

                      const iconMap = {
                        full_remove: Trash2,
                        full_replace: ArrowLeftRight,
                        partial_period_linear: TrendingUp,
                        partial_period_runrate: Activity,
                        discrete_event_delta: GitBranch,
                        smoothing: BarChart3,
                      };

                      return (
                        <div className="qoe-adjustment-details">
                          <div className="qoe-adjustment-details-label">Adjustment Details</div>
                          {adjs.map((adj, i) => {
                            const info = getCategoryInfo(adj.category);
                            const IconComponent = iconMap[adj.category] || HelpCircle;
                            const directionDisplay = adj.direction === 'add_back'
                              ? { symbol: '↑', text: 'Add-back', className: 'add-back' }
                              : adj.direction === 'deduction'
                                ? { symbol: '↓', text: 'Deduction', className: 'deduction' }
                                : { symbol: '↔', text: 'Neutral', className: 'neutral' };

                            return (
                              <div key={i} className="qoe-adjustment-detail">
                                <div className="qoe-adjustment-detail-header">
                                  <span className={`qoe-category-badge ${adj.category.replace(/_/g, '-')}`} style={{ backgroundColor: info.color }}>
                                    <IconComponent size={10} />
                                    {info.label}
                                  </span>
                                  <span className={`qoe-direction-indicator ${directionDisplay.className}`}>
                                    {directionDisplay.symbol} {directionDisplay.text}
                                  </span>
                                </div>
                                {adj.reasoning && (
                                  <div className="qoe-adjustment-reasoning">{adj.reasoning}</div>
                                )}
                                {adj.requires_input && adj.input_prompt && (
                                  <div className="qoe-input-callout">
                                    <Edit3 size={12} />
                                    <div>
                                      <div className="qoe-input-prompt">{adj.input_prompt}</div>
                                      {adj.input_needed?.length > 0 && (
                                        <ul className="qoe-input-needed-list">
                                          {adj.input_needed.map((field, fi) => (
                                            <li key={fi}>{field}</li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  </div>

                  {/* Period Analysis — pinned at bottom */}
                  {selectedQoeItem.period_values && Object.keys(selectedQoeItem.period_values).length > 0 && (
                    <div className="qoe-period-analysis">
                        {/* Period Grid - Left Side */}
                        <div className="qoe-period-grid">
                          <div className="qoe-period-grid-header">Period Breakdown</div>
                          <div className="qoe-period-list">
                            {Object.entries(selectedQoeItem.period_values).map(([period, value], index) => {
                              const numValue = parseFloat(value) || 0;
                              const hasValue = numValue !== 0;
                              const isHighlighted = highlightedPeriodIndex === index;
                              return (
                                <div
                                  key={period}
                                  className={`qoe-period-row ${hasValue ? 'has-value' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                                  data-period-index={index}
                                >
                                  <span className="qoe-period-index">{index + 1}</span>
                                  <span className="qoe-period-name">{period}</span>
                                  <span className={`qoe-period-value ${numValue < 0 ? 'negative' : ''}`}>
                                    {hasValue ? formatQoeCurrency(numValue) : '–'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Bar Chart - Right Side */}
                        <div className="qoe-chart-container">
                          <div className="qoe-chart-header">Trend Analysis</div>
                          {(() => {
                            const entries = Object.entries(selectedQoeItem.period_values);
                            const values = entries.map(([, v]) => parseFloat(v) || 0);
                            const nonZeroValues = values.filter(v => v !== 0);
                            const maxPositive = Math.max(...values, 0);
                            const maxNegative = Math.min(...values, 0);
                            const maxAbs = Math.max(Math.abs(maxPositive), Math.abs(maxNegative), 1);
                            const hasNegatives = maxNegative < 0;

                            const mean = nonZeroValues.length > 0
                              ? nonZeroValues.reduce((a, b) => a + b, 0) / nonZeroValues.length
                              : 0;

                            const handleBarClick = (e, index, period, value) => {
                              const wrapperRect = e.currentTarget.getBoundingClientRect();
                              const barEl = e.currentTarget.querySelector('.qoe-bar');
                              const barRect = barEl ? barEl.getBoundingClientRect() : wrapperRect;

                              if (highlightedPeriodIndex === index) {
                                setHighlightedPeriodIndex(null);
                                setPeriodTooltip({ visible: false, index: null, period: '', value: 0, x: 0, y: 0 });
                              } else {
                                setHighlightedPeriodIndex(index);
                                setPeriodTooltip({
                                  visible: true,
                                  index,
                                  period,
                                  value,
                                  x: wrapperRect.left + wrapperRect.width / 2,
                                  y: barRect.top - 10
                                });

                                const periodRow = document.querySelector(`[data-period-index="${index}"]`);
                                if (periodRow) {
                                  periodRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                }
                              }
                            };

                            return (
                              <div className={`qoe-bar-chart ${hasNegatives ? 'has-negatives' : ''}`}>
                                {periodTooltip.visible && (
                                  <div
                                    className="qoe-bar-tooltip"
                                    style={{
                                      left: `${periodTooltip.x}px`,
                                      top: `${periodTooltip.y}px`
                                    }}
                                  >
                                    <div className="qoe-tooltip-period">{periodTooltip.period}</div>
                                    <div className="qoe-tooltip-value">{formatQoeCurrency(periodTooltip.value)}</div>
                                    {mean !== 0 && (
                                      <div className="qoe-tooltip-comparison">
                                        {periodTooltip.value > mean
                                          ? `${((periodTooltip.value / mean - 1) * 100).toFixed(0)}% above avg`
                                          : periodTooltip.value < mean
                                            ? `${((1 - periodTooltip.value / mean) * 100).toFixed(0)}% below avg`
                                            : 'At average'
                                        }
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="qoe-bars-area">
                                  {hasNegatives && <div className="qoe-zero-line" />}

                                  {mean > 0 && nonZeroValues.length > 1 && (
                                    <div
                                      className="qoe-mean-line positive"
                                      style={{
                                        bottom: hasNegatives ? `${50 + (mean / maxAbs * 50)}%` : `${(mean / maxAbs * 100)}%`
                                      }}
                                      title={`Average (non-zero): ${formatQoeCurrency(mean)}`}
                                    >
                                      <span className="qoe-mean-label">avg {formatQoeCurrency(mean)}</span>
                                    </div>
                                  )}

                                  <div className="qoe-bars">
                                    {entries.map(([period, value], i) => {
                                      const numVal = parseFloat(value) || 0;
                                      const heightPct = Math.abs(numVal) / maxAbs * (hasNegatives ? 50 : 100);
                                      const isSpike = nonZeroValues.length > 1 && Math.abs(numVal) > Math.abs(mean) * 1.5 && numVal !== 0;
                                      const isNegative = numVal < 0;
                                      const isSelected = highlightedPeriodIndex === i;

                                      return (
                                        <div
                                          key={period}
                                          className={`qoe-bar-wrapper ${isNegative ? 'negative-wrapper' : 'positive-wrapper'} ${isSelected ? 'selected' : ''}`}
                                          onClick={(e) => handleBarClick(e, i, period, numVal)}
                                          title={`${period}: ${formatQoeCurrency(numVal)}`}
                                        >
                                          <div
                                            className={`qoe-bar ${isSpike ? 'spike' : ''} ${isNegative ? 'negative' : ''} ${numVal === 0 ? 'zero' : ''} ${isSelected ? 'selected' : ''}`}
                                            style={{ height: `${Math.max(heightPct, numVal === 0 ? 1 : 3)}%` }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div className="qoe-bar-labels">
                                  {entries.map(([period], i) => (
                                    <span
                                      key={period}
                                      className={`qoe-bar-label ${highlightedPeriodIndex === i ? 'highlighted' : ''}`}
                                      onClick={(e) => {
                                        const barWrapper = e.currentTarget.closest('.qoe-bar-chart').querySelector(`.qoe-bar-wrapper:nth-child(${i + 1})`);
                                        if (barWrapper) {
                                          handleBarClick({ currentTarget: barWrapper, ...e }, i, period, parseFloat(entries[i][1]) || 0);
                                        }
                                      }}
                                    >
                                      {i + 1}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                </>
              ) : (
                <div className="qoe-detail-empty">
                  <p>Select a candidate from the list to view details</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="qoe-no-items" style={{ gridColumn: '1 / -1', padding: '40px' }}>
            <p>No adjustment candidates identified. All items appear to be normal recurring expenses.</p>
          </div>
        )}
      </div>

      {/* Full-width GL Transactions section — synced to selected candidate */}
      <div className="qoe-full-gl-section">
        {glResults && selectedQoeItem ? (() => {
          const match = getGlMatch(selectedQoeItem.original_name);
          const breakdown = getAccountTransactions(selectedQoeItem.original_name);
          const adjustments = getAccountAdjustments(selectedQoeItem.original_name);

          if (!match) {
            if (glResults.unmatched_items?.includes(selectedQoeItem.original_name)) {
              return (
                <div className="gl-no-match-notice">
                  <AlertCircle size={14} />
                  <span>No matching GL account found for "{selectedQoeItem.original_name}"</span>
                </div>
              );
            }
            return (
              <div className="qoe-gl-placeholder">
                <span>Select a candidate to view GL transactions</span>
              </div>
            );
          }

          return (
            <>
              <div className="gl-section-header">
                <div className="gl-section-title">
                  <span>GL Transactions — {selectedQoeItem.original_name}</span>
                  <span className="gl-account-badge">
                    {match.gl_account_code} — {match.gl_account_name}
                  </span>
                </div>
                <div className="gl-section-stats">
                  <span>{breakdown?.transaction_count || 0} transactions</span>
                  {adjustments.length > 0 && (
                    <span className="gl-adj-count">{adjustments.length} adjustment(s)</span>
                  )}
                </div>
              </div>

              {breakdown?.transactions?.length > 0 && (
                <div className="gl-transaction-table-wrapper">
                  <table className="gl-transaction-table">
                    <thead>
                      <tr>
                        <th
                          className={`sortable ${glSortField === 'date' ? 'active' : ''}`}
                          onClick={() => { setGlSortField('date'); setGlSortAsc(glSortField === 'date' ? !glSortAsc : true); }}
                        >Date</th>
                        <th
                          className={`sortable ${glSortField === 'vendor' ? 'active' : ''}`}
                          onClick={() => { setGlSortField('vendor'); setGlSortAsc(glSortField === 'vendor' ? !glSortAsc : true); }}
                        >Vendor</th>
                        <th>Type</th>
                        <th
                          className={`sortable amount-col ${glSortField === 'amount' ? 'active' : ''}`}
                          onClick={() => { setGlSortField('amount'); setGlSortAsc(glSortField === 'amount' ? !glSortAsc : false); }}
                        >Amount</th>
                        <th>D/C</th>
                        <th>Adjustment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const usedAdjIdx = new Set();
                        const sortedTxns = sortGlTransactions(breakdown.transactions);
                        const txnAdjPairs = sortedTxns.map(txn => {
                          let bestAdj = null, bestScore = 0, bestIdx = -1;
                          adjustments.forEach((a, ai) => {
                            if (usedAdjIdx.has(ai)) return;
                            const amtClose = Math.abs(a.amount - txn.amount) < Math.max(0.01, txn.amount * 0.01);
                            if (!amtClose) return;
                            let score = 1;
                            const av = (a.vendor || '').toLowerCase().trim();
                            const tv = (txn.vendor || '').toLowerCase().trim();
                            if (av && tv && (av.includes(tv) || tv.includes(av))) score += 2;
                            if (a.date && txn.date && (a.date.includes(txn.date) || txn.date.includes(a.date))) score += 1;
                            if (score > bestScore) { bestScore = score; bestAdj = a; bestIdx = ai; }
                          });
                          if (bestAdj) usedAdjIdx.add(bestIdx);
                          return { txn, adj: bestAdj };
                        });
                        return txnAdjPairs.map(({ txn, adj }, i) => {
                          const isAdj = !!adj;
                          return (
                            <tr key={i} className={isAdj ? `gl-row-adjustment adj-${adj.adjustment_type.toLowerCase().replace(/[^a-z]/g, '-')}` : ''}>
                              <td className="gl-date">{txn.date}</td>
                              <td className="gl-vendor" title={txn.vendor}>{txn.vendor?.length > 30 ? txn.vendor.slice(0, 30) + '...' : txn.vendor}</td>
                              <td className="gl-type">{txn.transaction_type}</td>
                              <td className="gl-amount">{formatQoeCurrency(txn.amount)}</td>
                              <td className={`gl-dc ${txn.is_debit ? 'debit' : 'credit'}`}>{txn.is_debit ? 'DR' : 'CR'}</td>
                              <td className="gl-adj-cell">
                                {isAdj && (
                                  <span
                                    className="gl-adj-badge"
                                    style={{ backgroundColor: getAdjustmentColor(adj.adjustment_type) }}
                                    title={`${adj.direction}: ${adj.reasoning}`}
                                  >
                                    {adj.adjustment_type}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })() : (
          glResults ? (
            <div className="qoe-gl-placeholder">
              <span>Select a candidate to view GL transactions</span>
            </div>
          ) : (
            <div
              className={`qoe-gl-dropzone ${isDragging ? 'is-dragging' : ''} ${glAnalyzing ? 'is-analyzing' : ''}`}
              onClick={() => !glAnalyzing && glUploadInputRef.current?.click()}
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
              onDrop={handleGlDrop}
              role="button"
              tabIndex={0}
            >
              <input
                ref={glUploadInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleGlUploadPick}
              />
              {glAnalyzing ? (
                <>
                  <div className="button-spinner"></div>
                  <p className="qoe-gl-dropzone-title">Analyzing general ledger…</p>
                </>
              ) : (
                <>
                  <Upload size={28} className="qoe-gl-dropzone-icon" />
                  <p className="qoe-gl-dropzone-title">
                    If you want to see the general ledger transactions, you have to upload it here
                  </p>
                  <p className="qoe-gl-dropzone-subtitle">
                    Drag and drop your GL workbook, or click to browse
                    {originalGlFileName && <> · original: <code>{originalGlFileName}</code></>}
                  </p>
                </>
              )}
            </div>
          )
        )}
      </div>

      {/* Footer */}
      <div className="qoe-full-footer">
        {glResults?.adjustment_summary && (
          <div className="gl-adjustment-summary" style={{ border: 'none', padding: 0, background: 'none' }}>
            <div className="gl-summary-row">
              <span className="gl-summary-label">Total Add-backs</span>
              <span className="gl-summary-value add-back">+{formatQoeCurrency(glResults.adjustment_summary.total_add_backs)}</span>
            </div>
            <div className="gl-summary-row">
              <span className="gl-summary-label">Total Deductions</span>
              <span className="gl-summary-value deduction">-{formatQoeCurrency(glResults.adjustment_summary.total_deductions)}</span>
            </div>
            <div className="gl-summary-row net">
              <span className="gl-summary-label">Net EBITDA Adjustment</span>
              <span className={`gl-summary-value ${glResults.adjustment_summary.net_ebitda_adjustment >= 0 ? 'add-back' : 'deduction'}`}>
                {glResults.adjustment_summary.net_ebitda_adjustment >= 0 ? '+' : ''}{formatQoeCurrency(glResults.adjustment_summary.net_ebitda_adjustment)}
              </span>
            </div>
            {glResults.adjustment_summary.by_type && Object.keys(glResults.adjustment_summary.by_type).length > 0 && (
              <div className="gl-summary-by-type">
                {Object.entries(glResults.adjustment_summary.by_type).map(([type, total]) => (
                  <span key={type} className="gl-type-chip" style={{ borderColor: getAdjustmentColor(type) }}>
                    {type}: {formatQoeCurrency(total)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="qoe-full-footer-summary">
          <span className="qoe-summary">{qoeResults.summary}</span>
        </div>
      </div>
    </div>
  );
};

export default QoEAnalysisView;
